<template>
  <AuthView title="Visa Send">
    <v-form :ref="setFormRef" @submit.prevent="send">
      <AppRow unwrapped>
        <AppCol :cols="2">
          <TextField v-model.lazy="id" label="Order ID" :rules="[required]" />
        </AppCol>
        <AppCol>
          <VisaCountrySelect v-model="country" />
        </AppCol>
      </AppRow>
      <AppRow>
        <AppSelect v-model="selectedProductName" label="Visa Type" :items="productNames" :rules="[required]" />
        <QuantitySelect v-model="count" :rules="[required]" />
        <EmailField v-model="email" />
      </AppRow>
      <AppRow v-for="i in count" :key="i">
        <TextField v-model="etaNumbers[i - 1]" :label="`eTA #${i}`" :rules="[required]" />
        <ImageInput
          :label="`Visa #${i}`"
          :loading="loading"
          hint="File"
          @change="setFile(i - 1, $event)"
        />
      </AppRow>
      <AppBtn primary :loading type="submit">
        Submit
      </AppBtn>
    </v-form>
  </AuthView>
</template>

<script setup lang="ts">
import type { ProductName, CountryKey, VisaFile, Product } from '~/types';
import { snackbarInjectionKey } from '~/assets/injection-keys';
import { convertToBase64 } from '~/helpers/media';
import { buildArray } from '~/helpers/general';
import { getProducts, sendVisa } from '~/helpers/http';

definePageMeta({ middleware: 'admin' });

const snackbar = inject(snackbarInjectionKey)!;

const { required } = useValidation();
const { setFormRef, loading, submit, resetValidation } = useForm();

const id = ref('');
const country = ref<CountryKey>('india');
const products = ref<Product[]>([]);
const productNames = computed<ProductName[]>(() => products.value!.map(p => p.name));
const selectedProductName = ref<ProductName>(productNames.value[0]);
const count = ref(1);
const email = ref('');
const etaNumbers = ref<string[]>([]);
const base64Files = ref<string[]>([]);

watch(count, count => {
  etaNumbers.value = buildArray(count, '');
  base64Files.value = buildArray(count, '');
}, { immediate: true });

watch(country, async c => {
  const data = await getProducts(c);
  products.value = data!;
}, { immediate: true });

watch(productNames, ([name]) => {
  selectedProductName.value = name;
});

async function setFile(i: number, file: File | null) {
  if (!file) return;
  base64Files.value[i] = await convertToBase64(file);
}

function send() {
  submit(async () => {
    const ETAs: VisaFile[] = etaNumbers.value.map((num, i) => ({
      number: num,
      base64: base64Files.value[i]
    }));
    const success = await sendVisa({
      files: ETAs,
      orderId: id.value,
      email: email.value,
      productName: selectedProductName.value
    });
    if (!success) {
      throw new Error('Failed sending email');
    }
    snackbar.value!.showSuccess(`Sent ${ETAs.length} visas to ${email.value}`);
    reset();
  });
}

function reset() {
  count.value = 0;
  email.value = '';
  etaNumbers.value = [''];
  id.value = '';
  setTimeout(() => {
    resetValidation();
    count.value = 1;
  }, 100);
}
</script>